import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Package, ArrowRightLeft, Download, FileText, FileSpreadsheet, ShoppingBag, Droplet, Trash2, Loader2, Plus, BarChart3, RefreshCw, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styles from './StockOverview.module.css';
import ex from './StockOverviewExtras.module.css';
import { useStore } from '../../../context/StoreContext';
import { SMIIFY_API } from '../../../config/api';

const INVENTORY_API = `${SMIIFY_API}/inventory`;
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const statusOf = (item) => {
    const q = Number(item.stock?.currentQuantity) || 0;
    if (q <= 0) return 'OUT';
    if (q <= (Number(item.stock?.lowStockAlertAt) || 0)) return 'LOW';
    return 'GOOD';
};

const StockOverview = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const exportRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [usageFilter, setUsageFilter] = useState('ALL');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [inventoryList, setInventoryList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const storeId = activeStore?.id || activeStore?._id || localStorage.getItem('smiify_active_store_id');

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportRef.current && !exportRef.current.contains(event.target)) setShowExportMenu(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchInventory = useCallback(async () => {
        const token = localStorage.getItem('smiify_token');
        if (!token || !storeId || storeId === 'global_hq') {
            setInventoryList([]);
            setLoadError(storeId === 'global_hq' ? 'Select a branch to see its stock.' : '');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setLoadError('');
        try {
            const response = await fetch(`${INVENTORY_API}?storeId=${encodeURIComponent(storeId)}`, {
                headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) setInventoryList(data.data || []);
            else { setInventoryList([]); setLoadError(data.message || `Could not load stock (error ${response.status}).`); }
        } catch (error) {
            setInventoryList([]);
            setLoadError('Cannot reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId]);

    useEffect(() => { fetchInventory(); }, [fetchInventory]);

    const categories = useMemo(() => [...new Set(inventoryList.map((i) => i.basicInfo?.category).filter(Boolean))].sort(), [inventoryList]);

    const summary = useMemo(() => inventoryList.reduce((s, item) => {
        const q = Number(item.stock?.currentQuantity) || 0;
        s.units += q;
        s.cost += q * (Number(item.pricing?.purchasePrice) || 0);
        s.retail += q * (Number(item.pricing?.sellingPrice) || Number(item.pricing?.mrp) || 0);
        const st = statusOf(item);
        if (st === 'LOW') s.low += 1;
        if (st === 'OUT') s.out += 1;
        return s;
    }, { units: 0, cost: 0, retail: 0, low: 0, out: 0 }), [inventoryList]);

    const filteredData = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return inventoryList.filter((item) => {
            const b = item.basicInfo || {};
            if (q && ![b.productName, b.sku, b.brand, b.category].some((v) => String(v || '').toLowerCase().includes(q))) return false;
            if (statusFilter !== 'ALL' && statusOf(item) !== statusFilter) return false;
            if (usageFilter !== 'ALL' && item.usageType !== usageFilter) return false;
            if (categoryFilter !== 'ALL' && b.category !== categoryFilter) return false;
            return true;
        });
    }, [inventoryList, searchTerm, statusFilter, usageFilter, categoryFilter]);

    const hasFilters = statusFilter !== 'ALL' || usageFilter !== 'ALL' || categoryFilter !== 'ALL' || searchTerm;
    const clearFilters = () => { setStatusFilter('ALL'); setUsageFilter('ALL'); setCategoryFilter('ALL'); setSearchTerm(''); };

    const calculateDiscount = (mrp, sell) => (mrp && sell && mrp > sell ? `${Math.round(((mrp - sell) / mrp) * 100)}%` : '-');

    /* ----------------------------- export ----------------------------- */
    const exportRows = () => filteredData.map((item) => {
        const b = item.basicInfo || {}; const st = item.stock || {}; const p = item.pricing || {};
        return [
            b.brand || '-', `${b.productName || '-'}${b.size ? ` (${b.size}${b.measureUnit || ''})` : ''}`, b.sku || '-', b.category || '-', item.usageType || '-',
            p.purchasePrice || 0, p.mrp || 0, p.sellingPrice || 0, calculateDiscount(p.mrp, p.sellingPrice), p.taxRate || 0, p.hsnCode || '-',
            st.currentQuantity || 0, st.lowStockAlertAt || 0, (st.currentQuantity || 0) * (p.purchasePrice || 0), statusOf(item),
        ];
    });
    const HEADERS = ['Brand', 'Product Name', 'SKU', 'Category', 'Usage Type', 'Cost (Rs)', 'MRP (Rs)', 'Selling Price (Rs)', 'Discount', 'GST %', 'HSN Code', 'Current Stock', 'Min Stock', 'Stock Value (Rs)', 'Status'];
    const fileDate = new Date().toISOString().split('T')[0];

    const exportToCSV = () => {
        const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
        const csv = [HEADERS.map(esc).join(','), ...exportRows().map((r) => r.map(esc).join(','))].join('\n');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
        link.download = `Inventory_Report_${fileDate}.csv`;
        link.click();
        setShowExportMenu(false);
    };

    const exportToExcel = () => {
        const escHtml = (v) => String(v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
        const table = `<table border="1"><tr>${HEADERS.map((h) => `<th>${h}</th>`).join('')}</tr>${exportRows().map((r) => `<tr>${r.map((c) => `<td>${escHtml(c)}</td>`).join('')}</tr>`).join('')}</table>`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([`<html><head><meta charset="UTF-8"></head><body>${table}</body></html>`], { type: 'application/vnd.ms-excel' }));
        link.download = `Inventory_Report_${fileDate}.xls`;
        link.click();
        setShowExportMenu(false);
    };

    const getUsageBadge = (type) => {
        switch (type) {
            case 'RETAIL': return <span className={styles.badgeRetail}><ShoppingBag size={10} /> Retail</span>;
            case 'PROFESSIONAL': return <span className={styles.badgePro}><Droplet size={10} /> Pro</span>;
            case 'HYBRID': return <span className={styles.badgeBoth}><Package size={10} /> Hybrid</span>;
            case 'CONSUMABLE': return <span className={styles.badgeConsumable}><Trash2 size={10} /> Supply</span>;
            default: return <span className={styles.badgeBoth}>{type || 'Unknown'}</span>;
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.titleRow}>
                    <h1 className={styles.pageTitle}>Inventory Management</h1>
                </div>

                <div className={styles.toolbarRow}>
                    <div className={styles.toolbarLeft}>
                        <div className={styles.mainTabs}>
                            <button type="button" className={`${styles.simpleTabBtn} ${styles.activeTabBtn}`}><Package size={14} /> Stock Overview</button>
                            <button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/update')}><ArrowRightLeft size={14} /> Update & Movements</button>
<button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/vendors')}><Truck size={14} /> Vendors</button>
                            <button type="button" className={styles.simpleTabBtn} onClick={() => navigate('/inventory/analytics')}><BarChart3 size={14} /> Analytics</button>
                        </div>
                        <div className={styles.searchBox}>
                            <Search size={14} className={styles.searchIcon} />
                            <input type="text" placeholder="Search product, brand, category or SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
                        </div>
                    </div>

                    <div className={styles.toolbarRight}>
                        <button type="button" className={styles.outlineBtn} onClick={fetchInventory} title="Reload" aria-label="Reload"><RefreshCw size={14} /></button>
                        <div className={styles.exportContainer} ref={exportRef}>
                            <button type="button" className={styles.outlineBtn} onClick={() => setShowExportMenu(!showExportMenu)} disabled={isLoading || filteredData.length === 0}>
                                <Download size={14} /> Export
                            </button>
                            {showExportMenu && (
                                <div className={styles.exportDropdown}>
                                    <button type="button" onClick={exportToCSV} className={styles.exportOption}><FileText size={14} className={styles.csvIcon} /> CSV</button>
                                    <button type="button" onClick={exportToExcel} className={styles.exportOption}><FileSpreadsheet size={14} className={styles.excelIcon} /> Excel</button>
                                </div>
                            )}
                        </div>
                        <button type="button" className={styles.addProductBtn} onClick={() => navigate('/inventory/add')}><Plus size={14} /> Add Product</button>
                    </div>
                </div>
            </div>

            <div className={styles.contentArea}>
                {/* ---------------- KPI strip (click to filter) ---------------- */}
                <div className={ex.kpis}>
                    <button type="button" className={`${ex.kpi} ${statusFilter === 'ALL' ? ex.kpiActive : ''}`} onClick={() => setStatusFilter('ALL')}>
                        <small>Products</small><strong>{inventoryList.length}</strong><em>{summary.units.toLocaleString('en-IN')} units in stock</em>
                    </button>
                    <div className={ex.kpi}><small>Stock value (cost)</small><strong>{inr(summary.cost)}</strong><em>At purchase price</em></div>
                    <div className={ex.kpi}><small>Stock value (selling)</small><strong>{inr(summary.retail)}</strong><em>Margin {inr(summary.retail - summary.cost)}</em></div>
                    <button type="button" className={`${ex.kpi} ${ex.kpiMuted} ${statusFilter === 'LOW' ? ex.kpiActive : ''}`} onClick={() => setStatusFilter('LOW')}>
                        <small>Low stock</small><strong>{summary.low}</strong><em>At or below min level</em>
                    </button>
                    <button type="button" className={`${ex.kpi} ${ex.kpiDanger} ${statusFilter === 'OUT' ? ex.kpiActive : ''}`} onClick={() => setStatusFilter('OUT')}>
                        <small>Out of stock</small><strong>{summary.out}</strong><em>Need to reorder</em>
                    </button>
                </div>

                {/* ---------------- filters ---------------- */}
                <div className={ex.filters}>
                    <select className={ex.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Stock status">
                        <option value="ALL">All status</option><option value="GOOD">Good</option><option value="LOW">Low</option><option value="OUT">Empty</option>
                    </select>
                    <select className={ex.filterSelect} value={usageFilter} onChange={(e) => setUsageFilter(e.target.value)} aria-label="Usage type">
                        <option value="ALL">All usage</option><option value="RETAIL">Retail</option><option value="PROFESSIONAL">Professional</option><option value="HYBRID">Hybrid</option><option value="CONSUMABLE">Consumable</option><option value="SAMPLE">Sample</option>
                    </select>
                    <select className={ex.filterSelect} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Category">
                        <option value="ALL">All categories</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {hasFilters && <button type="button" className={ex.clearBtn} onClick={clearFilters}>Clear filters</button>}
                    <span className={ex.filterCount}>Showing {filteredData.length} of {inventoryList.length}</span>
                </div>

                <div className={styles.tableContainer}>
                    <div className={styles.tableScrollArea}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Brand</th><th>Product Name</th><th>SKU</th><th>Category</th><th>Usage Type</th>
                                    <th className={styles.textRight}>Cost (₹)</th><th className={styles.textRight}>MRP (₹)</th><th className={styles.textRight}>Sell (₹)</th>
                                    <th className={styles.textRight}>Discount</th><th className={styles.textRight}>GST %</th><th>HSN Code</th>
                                    <th className={styles.textRight}>Stock</th><th className={styles.textRight}>Min Limit</th><th className={styles.textRight}>Value (₹)</th>
                                    <th className={styles.textCenter}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="15" style={{ textAlign: 'center', padding: '40px' }}><Loader2 className="animate-spin" size={24} style={{ margin: '0 auto', color: 'var(--h-color-primary-600)' }} /></td></tr>
                                ) : loadError ? (
                                    <tr><td colSpan="15" style={{ textAlign: 'center', padding: '40px', color: 'var(--h-color-danger-600)' }}>{loadError}</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan="15" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>{inventoryList.length ? 'No products match these filters.' : 'No products yet. Add your first product.'}</td></tr>
                                ) : (
                                    filteredData.map((item) => {
                                        const b = item.basicInfo || {}; const st = item.stock || {}; const p = item.pricing || {};
                                        const status = statusOf(item);
                                        return (
                                            <tr key={item._id} className={`${styles.mainRow} ${ex.rowClickable}`} onClick={() => navigate(`/inventory/update?product=${item._id}`)} title="Click to update stock">
                                                <td className={styles.brandName}>{b.brand || '-'}</td>
                                                <td>
                                                    <span className={styles.productName}>{b.productName || '-'}</span>
                                                    {b.size ? <span className={styles.sizeText}>({b.size}{b.measureUnit})</span> : null}
                                                </td>
                                                <td className={styles.skuText}>{b.sku || '-'}</td>
                                                <td className={styles.categoryText}>{b.category || '-'}</td>
                                                <td>{getUsageBadge(item.usageType)}</td>
                                                <td className={`${styles.priceCell} ${styles.textRight}`}>{p.purchasePrice || 0}</td>
                                                <td className={`${styles.mutedCell} ${styles.textRight}`}>{p.mrp || 0}</td>
                                                <td className={`${styles.priceCell} ${styles.textRight}`}>{p.sellingPrice || 0}</td>
                                                <td className={`${styles.discountCell} ${styles.textRight}`}>{calculateDiscount(p.mrp, p.sellingPrice)}</td>
                                                <td className={`${styles.taxCell} ${styles.textRight}`}>{p.taxRate || 0}%</td>
                                                <td className={styles.hsnText}>{p.hsnCode || '-'}</td>
                                                <td className={`${styles.stockLarge} ${styles.textRight} ${status === 'OUT' ? styles.txtDanger : status === 'LOW' ? styles.txtWarning : styles.txtSuccess}`}>{st.currentQuantity || 0}</td>
                                                <td className={`${styles.stockMinText} ${styles.textRight}`}>{st.lowStockAlertAt || 0}</td>
                                                <td className={`${styles.priceCell} ${styles.textRight}`}>{((st.currentQuantity || 0) * (p.purchasePrice || 0)).toLocaleString('en-IN')}</td>
                                                <td className={styles.textCenter}>
                                                    {status === 'OUT' ? <span className={styles.dangerBadge}>EMPTY</span> : status === 'LOW' ? <span className={styles.warningBadge}>LOW</span> : <span className={styles.successBadge}>GOOD</span>}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockOverview;
